import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { DialogData } from '../../../../shared/models/dialog-data.model';
import { InputField } from '../../../../shared/components/input-field/input-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { WebsiteGroupsService } from '../../../../core/services/website-groups.service';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { AuthService } from '../../../../core/services/auth.service';
import { WebsitesService } from '../../../../core/services/websites.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { Control, FormType, WebsiteFormValue } from './add-website-dialog.models';
import {
  ALIAS_CONTROLS,
  MAIN_SITE_CONTROLS,
  SITE_CONTROLS,
  SUBDOMAIN_CONTROLS,
  TYPES,
  WP_FORM_ERROR_MESSAGES,
} from './add-website-dialog.const';
import {
  CreateMainWebsiteDTO,
  CreateWebsiteAliasDTO,
  CreateWebsiteDTO,
  CreateWebsiteSubdomainDTO,
} from '../../../../shared/models/website-create.models';
import { TemporaryWPCredentials } from '../../../../shared/models/wordpress.models';
import { WebsiteGroupItem } from '../../../../shared/models/website-group.models';
import {
  mapToAliasCreateRequest,
  mapToMainWebsiteCreateRequest,
  mapToSubdomainCreateRequest,
  mapToWebsiteCreateRequest,
  mapToWPCredentialsRequest,
} from './website-request.mapper';

@Component({
  selector: 'app-add-website-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    InputField,
    MatCheckboxModule,
    AsyncPipe,
  ],
  templateUrl: './add-website-dialog.component.html',
  styleUrl: './add-website-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddWebsiteDialogComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<AddWebsiteDialogComponent>);

  readonly data = inject<
    DialogData & {
      servers: { label: string; value: string }[];
      plans: { label: string; value: string }[];
    }
  >(MAT_DIALOG_DATA);

  private fb = inject(FormBuilder);

  private websitesService = inject(WebsitesService);

  private websiteGroupsService = inject(WebsiteGroupsService);

  private authService = inject(AuthService);

  step = 1;

  title = 'Add Website';

  primaryButtonText = 'Add Website';

  currentUser = this.authService.getUser()!;

  readonly types = TYPES;

  siteControls: Control[] = SITE_CONTROLS;

  mainSiteControls: Control[] = MAIN_SITE_CONTROLS;

  aliasControls: Control[] = ALIAS_CONTROLS;

  subdomainControls: Control[] = SUBDOMAIN_CONTROLS;

  readonly wpErrors = WP_FORM_ERROR_MESSAGES;

  controlsArray = this.siteControls;

  form = this.fb.nonNullable.group({
    type: [FormType.SITE.toString(), Validators.required], // Utility control
    websiteId: [''],
    originServer: [''],
    groupId: [''],
    domain: ['', [this.domainValidator]],
    subdomainMain: [''], // Utility control
    userId: [''],
    plan: [''],
    enableEmail: [true], // Utility control
    installWordpress: [false], // Utility control
  });

  wordpressForm = this.fb.nonNullable.group({
    domain: [''],
    user: ['', Validators.required],
    password: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    // directory: [''],
    enableRedisCache: [false],
    enableW3TotalCache: [false], // Utility control
    enableWooCommerce: [false], // Utility control
    plugins: [],
  });

  websiteSearchText = signal<string>('');

  websiteSearchText$ = toObservable(this.websiteSearchText);

  websites$ = this.websiteSearchText$.pipe(
    debounceTime(500),
    distinctUntilChanged(),
    switchMap((domain) => this.websitesService.searchWebsites({ domainSearch: domain })),
    map(({ Websites }) => Websites.map((w) => ({ label: w.domain, value: String(w.id) }))),
    shareReplay(1),
  );

  groupSearchText = signal<string>('');

  groupSearchText$ = toObservable(this.groupSearchText);

  serverGroups$ = this.form.controls.originServer.valueChanges.pipe(
    filter((x) => !!x),
    switchMap((server) =>
      this.websiteGroupsService.getServerGroups(
        server,
        this.currentUser.isAdmin ? undefined : this.currentUser.id,
      ),
    ),
    shareReplay(1),
  );

  groupsOptions$ = combineLatest({
    groups: this.serverGroups$,
    searchText: this.groupSearchText$,
  }).pipe(
    map(({ groups, searchText }) =>
      groups
        .map((g) => ({ label: this.getGroupDisplayName(g) || g.owner, value: String(g.id) }))
        .filter((g) => !searchText || g.label.includes(searchText)),
    ),
  );

  websiteSuffixText$ = this.form.controls.websiteId.valueChanges.pipe(
    withLatestFrom(this.websites$),
    map(([id, websites]) => {
      const domain = websites.find((w) => w.value === id)?.label;

      if (this.form.controls.type.value === FormType.SUBDOMAIN.toString() && domain)
        return `.${domain}`;
      return '';
    }),
    tap((domain) => this.form.controls.subdomainMain.setValue(domain)),
  );

  ownerSearchText = signal<string>('');

  ownerSearchText$ = toObservable(this.ownerSearchText);

  ownerOptions$ = this.ownerSearchText$.pipe(
    debounceTime(500),
    distinctUntilChanged(),
    switchMap((search) => this.authService.getUsers({ search })),
    map(([users]) => users.map((user) => ({ label: user.username, value: user.id }))),
  );

  serverSearchText = signal<string>('');

  servers = computed(() =>
    this.data.servers.filter((s) =>
      this.serverSearchText() ? s.label.includes(this.serverSearchText()) : true,
    ),
  );

  get plans(): { label: string; value: string }[] {
    return this.data.plans;
  }

  get type(): string {
    return this.form.controls.type.getRawValue();
  }

  ngOnInit(): void {
    this.listenForChanges();
    this.updateValidators(FormType.SITE.toString());

    if (!this.currentUser.isAdmin) {
      this.mainSiteControls = this.mainSiteControls.filter((c) => c.controlName !== 'userId');
      this.controlsArray = this.siteControls;
    }
  }

  listenForChanges(): void {
    this.form.controls.installWordpress.valueChanges.subscribe((enabled) => {
      this.primaryButtonText = enabled ? 'Continue' : 'Add Website';
    });

    this.form.controls.type.valueChanges.subscribe((type) => {
      this.form.reset({ type }, { emitEvent: false });
      this.updateValidators(type);
      this.updateForm(type);
    });

    this.form.controls.originServer.valueChanges.subscribe((server) => {
      if (server) this.form.controls.groupId.enable();
      else this.form.controls.groupId.disable();
      this.form.controls.groupId.updateValueAndValidity();
    });

    this.form.controls.groupId.valueChanges
      .pipe(withLatestFrom(this.serverGroups$))
      .subscribe(([groupId, groups]) => {
        const selectedGroup = groups.find((g) => g.id === +groupId);

        if (selectedGroup) {
          this.form.controls.userId.setValue(selectedGroup.owner);
          this.form.controls.userId.disable();
          this.form.controls.userId.updateValueAndValidity();
        } else {
          this.form.controls.userId.enable();
        }
      });

    this.form.controls.domain.valueChanges.subscribe((domain) =>
      this.wordpressForm.controls.domain.setValue(domain),
    );
  }

  updateValidators(type: string): void {
    const siteControls = SITE_CONTROLS.filter((c) => c.type !== 'checkbox').map(
      (c) => c.controlName,
    );
    const mainSiteControls = MAIN_SITE_CONTROLS.filter((c) => c.type !== 'checkbox').map(
      (c) => c.controlName,
    );
    const aliasControls = ALIAS_CONTROLS.filter((c) => c.type !== 'checkbox').map(
      (c) => c.controlName,
    );
    const subdomainControls = SUBDOMAIN_CONTROLS.filter((c) => c.type !== 'checkbox').map(
      (c) => c.controlName,
    );

    Object.values(this.form.controls).forEach((control) => {
      control.clearValidators();
      control.updateValueAndValidity({ emitEvent: false });
    });

    switch (type) {
      case FormType.MAIN_SITE.toString():
        mainSiteControls.forEach((control) => {
          this.form.get(control)?.setValidators(Validators.required);
          this.form.controls.domain.addValidators(this.domainValidator);
          this.form.get(control)?.updateValueAndValidity();
        });
        break;
      case FormType.ALIAS.toString():
        aliasControls.forEach((control) => {
          this.form.get(control)?.setValidators(Validators.required);
          this.form.controls.domain.addValidators(this.domainValidator);
          this.form.get(control)?.updateValueAndValidity();
        });
        break;
      case FormType.SUBDOMAIN.toString():
        subdomainControls.forEach((control) => {
          this.form.get(control)?.setValidators(Validators.required);
          this.form.get(control)?.updateValueAndValidity();
        });
        break;
      default:
        siteControls.forEach((control) => {
          this.form.get(control)?.setValidators(Validators.required);
          this.form.controls.domain.addValidators(this.domainValidator);
          this.form.get(control)?.updateValueAndValidity();
        });
        break;
    }
  }

  updateForm(type: string): void {
    switch (type) {
      case FormType.MAIN_SITE.toString():
        this.controlsArray = this.mainSiteControls;
        break;
      case FormType.ALIAS.toString():
        this.controlsArray = this.aliasControls;
        break;
      case FormType.SUBDOMAIN.toString():
        this.controlsArray = this.subdomainControls;
        break;
      default:
        this.controlsArray = this.siteControls;
        break;
    }
  }

  submit(): void {
    const installWp = this.form.controls.installWordpress.value;

    if (this.step === 1 && installWp) {
      this.step = 2;

      this.title = 'Install WordPress';
      this.primaryButtonText = 'Add Website';

      return;
    }

    const result: {
      type: FormType;
      website:
        | CreateWebsiteDTO
        | CreateMainWebsiteDTO
        | CreateWebsiteAliasDTO
        | CreateWebsiteSubdomainDTO;
      wordpress?: TemporaryWPCredentials;
    } = {
      type: FormType[this.form.controls.type.getRawValue() as keyof typeof FormType],
      website: this.getWebsiteRequestData(this.form.getRawValue()),
    };
    if (this.step === 2)
      result.wordpress = mapToWPCredentialsRequest(this.wordpressForm.getRawValue());

    this.dialogRef.close(result);
  }

  private getWebsiteRequestData(
    formValue: WebsiteFormValue,
  ): CreateWebsiteDTO | CreateMainWebsiteDTO | CreateWebsiteAliasDTO | CreateWebsiteSubdomainDTO {
    switch (formValue.type) {
      case FormType.MAIN_SITE.toString():
        return mapToMainWebsiteCreateRequest(formValue, this.currentUser);
      case FormType.ALIAS.toString():
        return mapToAliasCreateRequest(formValue);
      case FormType.SUBDOMAIN.toString():
        return mapToSubdomainCreateRequest(formValue);
      default:
        return mapToWebsiteCreateRequest(formValue);
    }
  }

  private domainValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    const isValid = !!value.match(/\w{3,}\.[a-z]{2,}/);
    return isValid ? null : { invalidDomain: true };
  }

  private getGroupDisplayName(group: WebsiteGroupItem): string {
    const plan = this.plans.find((p) => +p.value === group.plan);
    return `${group.id} - ${group.mainDomain} - ${plan?.label}`;
  }

  close(): void {
    this.dialogRef.close();
  }
}
